import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { B4Expr } from '@precon-tools-suite-by-shared/gino-multi-valued-logic-purescript';

export enum NodeKind {
    Value = 'value',
    Term = 'term',
    Conj = 'conj',
    Disj = 'disj',
    Neg = 'neg',
    Implies = 'implies',
    Equiv = 'equiv',
}

export type ValueRepr<T> = {
    kind: NodeKind.Value;
    value: PredicateValue;
    children: NodeRepr<T>[];
};

export type TermRepr<T> = {
    kind: NodeKind.Term;
    term: T | null;
    children: NodeRepr<T>[];
};

export type UnaryRepr<T> = {
    kind: NodeKind.Neg;
    child: NodeRepr<T>;
};

export type BinRepr<T> = {
    kind: NodeKind.Implies | NodeKind.Equiv;
    lhs: NodeRepr<T>;
    rhs: NodeRepr<T>;
};

export type NaryRepr<T> = {
    kind: NodeKind.Conj | NodeKind.Disj;
    children: NodeRepr<T>[];
};

/**
 * The internal representation of an expression node for the inputs.
 */
export type NodeRepr<T = string> =
    | ValueRepr<T>
    | TermRepr<T>
    | UnaryRepr<T>
    | BinRepr<T>
    | NaryRepr<T>;

export type NodeReprWithKind<K extends NodeKind, T = string> = {
    [NodeKind.Value]: ValueRepr<T> & { kind: NodeKind.Value };
    [NodeKind.Term]: TermRepr<T> & { kind: NodeKind.Term };
    [NodeKind.Conj]: NaryRepr<T> & { kind: NodeKind.Conj };
    [NodeKind.Disj]: NaryRepr<T> & { kind: NodeKind.Disj };
    [NodeKind.Neg]: UnaryRepr<T> & { kind: NodeKind.Neg };
    [NodeKind.Implies]: BinRepr<T> & { kind: NodeKind.Implies };
    [NodeKind.Equiv]: BinRepr<T> & { kind: NodeKind.Equiv };
}[K];

function exprToPredicateValue(expr: B4Expr): PredicateValue {
    switch (expr.toString()) {
        case 'Y':
            return PredicateValue.Yes;
        case 'N':
            return PredicateValue.No;
        case 'U':
            return PredicateValue.Undecidable;
        default:
            return PredicateValue.Indeterminate;
    }
}

function predicateValueToExpr(val: PredicateValue): B4Expr {
    switch (val) {
        case PredicateValue.Yes:
            return B4Expr.YES;
        case PredicateValue.No:
            return B4Expr.NO;
        case PredicateValue.Undecidable:
            return B4Expr.UNDECIDABLE;
        case PredicateValue.Indeterminate:
            return B4Expr.INDETERMINATE;
        default:
            return B4Expr.from(val);
    }
}

export function reprFromExpr(expr: B4Expr): NodeRepr {
    switch (expr.nodeType) {
        case 'Leaf':
            if (expr.isValue()) {
                return createValueRepr(exprToPredicateValue(expr));
            } else {
                return createTermRepr(expr.terms[0] ?? null);
            }
        case 'And':
            return {
                kind: NodeKind.Conj,
                children: expr.children.map(reprFromExpr),
            };
        case 'Or':
            return {
                kind: NodeKind.Disj,
                children: expr.children.map(reprFromExpr),
            };
        case 'Neg':
            return {
                kind: NodeKind.Neg,
                child: reprFromExpr(expr.children[0]),
            };
        case 'Implies': {
            const [lhs, rhs] = expr.children;
            return {
                kind: NodeKind.Implies,
                lhs: reprFromExpr(lhs),
                rhs: reprFromExpr(rhs),
            };
        }
        case 'Equiv': {
            const [lhs, rhs] = expr.children;
            return {
                kind: NodeKind.Equiv,
                lhs: reprFromExpr(lhs),
                rhs: reprFromExpr(rhs),
            };
        }
    }
}

function _exprFromRepr<T>(
    repr: NodeRepr<T>,
    termToStr: (termVal: T) => string | null
): B4Expr | null {
    switch (repr.kind) {
        case NodeKind.Value:
            return predicateValueToExpr(repr.value);
        case NodeKind.Term: {
            if (!repr.term) return null;
            const term = termToStr(repr.term);
            if (term === null) return null;
            return B4Expr.term(term);
        }
        case NodeKind.Neg: {
            const child = _exprFromRepr(repr.child, termToStr);
            if (!child) return null;
            return child.not();
        }
        case NodeKind.Implies: {
            const lhs = _exprFromRepr(repr.lhs, termToStr);
            const rhs = _exprFromRepr(repr.rhs, termToStr);
            if (lhs === null && rhs === null) return null;
            return B4Expr.implies(lhs, rhs);
        }
        case NodeKind.Equiv: {
            const lhs = _exprFromRepr(repr.lhs, termToStr);
            const rhs = _exprFromRepr(repr.rhs, termToStr);
            if (lhs === null && rhs === null) return null;
            if (lhs === null || rhs === null) return B4Expr.YES;
            return B4Expr.equiv(lhs, rhs);
        }
        case NodeKind.Conj: {
            const children = repr.children
                .map((c) => _exprFromRepr(c, termToStr))
                .filter((c) => c !== null);
            if (children.length === 0) return null;
            if (children.length === 1) return children[0];
            return B4Expr.and(...children);
        }
        case NodeKind.Disj: {
            const children = repr.children
                .map((c) => _exprFromRepr(c, termToStr))
                .filter((c) => c !== null);
            if (children.length === 0) return null;
            if (children.length === 1) return children[0];
            return B4Expr.or(...children);
        }
    }
}

export function exprFromRepr(repr: NodeRepr): B4Expr | null;
export function exprFromRepr<T>(
    repr: NodeRepr<T>,
    termToStr: (termVal: T) => string | null
): B4Expr | null;
export function exprFromRepr<T>(
    repr: NodeRepr<T>,
    termToStr?: (termVal: T) => string | null
): B4Expr | null {
    const raw = _exprFromRepr(repr, termToStr ?? ((t) => t as string));
    if (raw === null) return raw;
    return raw.elimClassicalValues();
}

export function createTermRepr<T = string>(
    term: T | null = null,
    children: NodeRepr<T>[] = []
): TermRepr<T> {
    return {
        kind: NodeKind.Term,
        term,
        children,
    };
}

export function createValueRepr<T = string>(
    value: PredicateValue = PredicateValue.Indeterminate,
    children: NodeRepr<T>[] = []
): NodeRepr<T> {
    return {
        kind: NodeKind.Value,
        value,
        children,
    };
}

export function createEmptyReprWithKind<K extends NodeKind, T = string>(
    kind: K
): NodeReprWithKind<K, T> {
    switch (kind) {
        case NodeKind.Value:
            return createValueRepr() as NodeReprWithKind<K, T>;
        case NodeKind.Term:
            return createTermRepr() as NodeReprWithKind<K, T>;
        case NodeKind.Disj:
        case NodeKind.Conj:
            return {
                kind,
                children: [] as NodeRepr<T>[],
            } as NodeReprWithKind<K, T>;
        case NodeKind.Implies:
        case NodeKind.Equiv:
            return {
                kind,
                lhs: createEmptyReprWithKind(NodeKind.Term),
                rhs: createEmptyReprWithKind(NodeKind.Term),
            } as NodeReprWithKind<K, T>;
        case NodeKind.Neg:
            return {
                kind,
                child: createEmptyReprWithKind(NodeKind.Term),
            } as NodeReprWithKind<K, T>;
        default:
            throw new Error(`Unkown internal repr kind '${kind}'.`);
    }
}

export function isValueRepr<T>(repr: NodeRepr<T>): repr is ValueRepr<T> {
    return repr.kind === NodeKind.Value;
}

export function isTermRepr<T>(repr: NodeRepr<T>): repr is TermRepr<T> {
    return repr.kind === NodeKind.Term;
}

export function isUnaryRepr<T>(repr: NodeRepr<T>): repr is UnaryRepr<T> {
    return repr.kind === NodeKind.Neg;
}

export function isBinRepr<T>(repr: NodeRepr<T>): repr is BinRepr<T> {
    return repr.kind === NodeKind.Implies || repr.kind === NodeKind.Equiv;
}

export function isNaryRepr<T>(repr: NodeRepr<T>): repr is NaryRepr<T> {
    return repr.kind === NodeKind.Conj || repr.kind === NodeKind.Disj;
}

export function isReprWithKind<K extends NodeKind, T>(
    kind: K,
    repr: NodeRepr<T>
): repr is NodeReprWithKind<K, T> {
    return repr.kind === kind;
}

export function reprGetChildren<T = string>(repr: NodeRepr<T>): NodeRepr<T>[] {
    if (isNaryRepr(repr) || isValueRepr(repr) || isTermRepr(repr))
        return repr.children;

    if (isUnaryRepr(repr)) return [repr.child];

    if (isBinRepr(repr)) return [repr.lhs, repr.rhs];

    return [];
}

export function reprGetLhsChild<T = string>(repr: NodeRepr<T>): NodeRepr<T> {
    if (isNaryRepr(repr) || isValueRepr(repr) || isTermRepr(repr))
        return repr.children[0] ?? createTermRepr();

    if (isUnaryRepr(repr)) return repr.child;

    if (isBinRepr(repr)) return repr.lhs;

    return createTermRepr();
}

export function reprGetRhsChild<T = string>(repr: NodeRepr<T>): NodeRepr<T> {
    if (isNaryRepr(repr) || isValueRepr(repr) || isTermRepr(repr))
        return repr.children[1] ?? createTermRepr();

    if (isBinRepr(repr)) return repr.rhs;

    return createTermRepr();
}

export function reprGetTerm<T = string>(repr: NodeRepr<T>): T | null {
    if (isTermRepr(repr)) return repr.term;
    if (isValueRepr(repr)) return null;

    return reprGetTerm(reprGetLhsChild(repr));
}

export function reprGetValue<T = string>(repr: NodeRepr<T>): PredicateValue {
    if (isTermRepr(repr)) return PredicateValue.Indeterminate;
    if (isValueRepr(repr)) return repr.value;

    return (
        exprFromRepr(repr, (t) => JSON.stringify(t))?.evaluated ??
        PredicateValue.Indeterminate
    );
}

export function reprWithTerm<T = string>(
    term: T | null,
    repr: NodeRepr<T>
): TermRepr<T> {
    if (!isTermRepr(repr)) {
        repr = reprWithNodeKind(NodeKind.Term, repr);
    }

    return {
        ...repr,
        term,
    };
}

export function reprWithValue<T = string>(
    value: PredicateValue,
    repr: NodeRepr<T>
): ValueRepr<T> {
    if (!isValueRepr(repr)) {
        repr = reprWithNodeKind(NodeKind.Value, repr);
    }

    return {
        ...repr,
        value,
    };
}

export function reprWithLhsChild<R extends NodeRepr<T>, T = string>(
    child: NodeRepr<T>,
    repr: R
): R {
    if (isNaryRepr(repr) || isValueRepr(repr) || isTermRepr(repr)) {
        const newChildren = [...repr.children];
        newChildren[0] = child;
        return reprWithChildren(newChildren, repr) as R;
    }

    if (isUnaryRepr(repr)) {
        return {
            ...repr,
            child,
        };
    }

    if (isBinRepr(repr)) {
        return {
            ...repr,
            lhs: child,
        };
    }

    return repr;
}

export function reprWithRhsChild<R extends NodeRepr<T>, T = string>(
    child: NodeRepr<T>,
    repr: R
): R {
    if (isNaryRepr(repr) || isValueRepr(repr) || isTermRepr(repr)) {
        const newChildren = [...repr.children];
        newChildren[1] = child;
        return reprWithChildren(newChildren, repr) as R;
    }

    if (isBinRepr(repr)) {
        return {
            ...repr,
            rhs: child,
        };
    }

    return repr;
}

export function reprWithChildren<R extends NodeRepr<T>, T = string>(
    children: (NodeRepr<T> | null | undefined)[],
    repr: R
): R | undefined {
    if (isNaryRepr(repr)) {
        return {
            kind: repr.kind,
            children: flattenChildren(
                repr.kind,
                children.filter((child): child is NodeRepr<T> => !!child)
            ),
        } as R;
    }

    if (isValueRepr(repr) || isTermRepr(repr))
        return {
            ...repr,
            children: children.filter((child) => !!child),
        } as R;

    if (isUnaryRepr(repr)) {
        const child = children[0];
        if (!child) return undefined;
        return {
            ...repr,
            child,
        };
    }

    if (isBinRepr(repr)) {
        const lhs = children[0];
        const rhs = children[1];

        if (!lhs && !rhs) return undefined;

        return {
            ...repr,
            lhs: lhs ?? createTermRepr(),
            rhs: rhs ?? createTermRepr(),
        };
    }
}

export function flattenChildren<T>(
    kind: NodeKind.Conj | NodeKind.Disj,
    children: NodeRepr<T>[]
): NodeRepr<T>[] {
    const result: NodeRepr<T>[] = [];
    for (const child of children) {
        if (child.kind === kind) {
            result.push(...flattenChildren(child.kind, child.children));
        } else {
            result.push(child);
        }
    }
    return result;
}

export function* reprDescendants<T>(repr: NodeRepr<T>) {
    for (const child of reprGetChildren(repr)) {
        yield child;
        yield* reprDescendants(child);
    }
}

export function reprIsChildOf<T>(
    parent: NodeRepr<T>,
    child: NodeRepr<T>
): boolean {
    for (const pChild of reprDescendants(parent)) {
        if (pChild === child) return true;
    }
    return false;
}

export function isSubPathOf(parent: any[], child: any[]): boolean {
    if (parent.length === 0) return true;
    if (child.length === 0) return false;
    const [parentHead, ...parentTail] = parent;
    const [childHead, ...childTail] = child;
    if (parentHead !== childHead) return false;
    return isSubPathOf(parentTail, childTail);
}

export function copyRepr<T = string, R extends NodeRepr<T> = NodeRepr<T>>(
    repr: R,
    withHiddenValues = false
): R {
    if (isTermRepr(repr) || isValueRepr(repr)) {
        return {
            ...repr,
            children: withHiddenValues
                ? repr.children.map((child) => copyRepr<T>(child, true))
                : [],
        } as R;
    }

    if (isNaryRepr(repr)) {
        return {
            ...repr,
            children: repr.children.map((child) =>
                copyRepr<T>(child, withHiddenValues)
            ),
        };
    }

    if (isUnaryRepr(repr)) {
        return {
            ...repr,
            child: copyRepr<T>(repr.child, withHiddenValues),
        };
    }

    if (isBinRepr(repr)) {
        return {
            ...repr,
            lhs: copyRepr<T>(repr.lhs, withHiddenValues),
            rhs: copyRepr<T>(repr.rhs, withHiddenValues),
        };
    }

    throw Error('Invalid repr');
}

export function reprMaybeGetPath<T>(
    root: NodeRepr<T> | undefined,
    path: number[]
): NodeRepr<T> | undefined {
    if (!root) return undefined;
    if (path.length === 0) {
        return root;
    }

    const [head, ...tail] = path;
    const children = reprGetChildren(root);
    return reprMaybeGetPath(children[head], tail);
}

export function reprGetPath<T>(root: NodeRepr<T>, path: number[]): NodeRepr<T> {
    const result = reprMaybeGetPath(root, path);
    if (result) return result;
    console.error('Encountered invalid path for NodeRepr', { root, path });
    throw Error('node-repr-path-not-found');
}

export function reprWithChildAt<
    T = string,
    R extends NodeRepr<T> = NodeRepr<T>
>(parent: R, ix: number, child: NodeRepr<T> | undefined): R {
    const children = reprGetChildren(parent);
    if (!child) {
        return reprWithChildren<R, T>(children.toSpliced(ix, 1), parent) as R;
    } else {
        return reprWithChildren<R, T>(
            children.toSpliced(ix, 1, child),
            parent
        ) as R;
    }
}

export function reprWithChildAtPath<T = string>(
    parent: NodeRepr<T>,
    path: number[],
    newValue: NodeRepr<T> | undefined
): NodeRepr<T> {
    if (path.length === 0) return newValue ?? parent;

    const [head, ...tail] = path;

    const child = reprGetChildren(parent)[head];
    if (!child) return reprWithChildAt(parent, head, newValue);

    return reprWithChildAt(
        parent,
        head,
        reprWithChildAtPath<T>(child, tail, newValue)
    );
}

export function reprSwapPath<T = string>(
    root: NodeRepr<T>,
    a: number[],
    b: number[]
): NodeRepr<T> {
    if (a.length === 0) return reprGetPath(root, b);
    if (b.length === 0) return reprGetPath(root, a);

    if (a[0] === b[0]) {
        const [head, ...aTail] = a;
        const [, ...bTail] = b;
        const children = reprGetChildren(root);
        const child = children[head];
        if (!child) return root;
        return reprWithChildAt<T>(
            root,
            head,
            reprSwapPath(child, aTail, bTail)
        );
    }

    const childAtA = reprMaybeGetPath(root, a);
    const childAtB = reprMaybeGetPath(root, b);

    return reprWithChildAtPath<T>(
        reprWithChildAtPath<T>(root, b, childAtA),
        a,
        childAtB
    );
}

export function reprWithNodeKind<K extends NodeKind, T = string>(
    newKind: K,
    repr: NodeRepr<T>
): NodeReprWithKind<K, T> {
    if (isReprWithKind(newKind, repr)) return repr;

    if (newKind === NodeKind.Neg)
        return {
            kind: NodeKind.Neg,
            child: repr,
        } as NodeReprWithKind<K, T>;

    const oldKind = repr.kind;

    switch (oldKind) {
        case NodeKind.Value: {
            switch (newKind) {
                case NodeKind.Term: {
                    const [head, ...tail] = repr.children;
                    if (head && isTermRepr(head)) {
                        return {
                            kind: NodeKind.Term,
                            term: head.term,
                            children: [...head.children, ...tail],
                        } as NodeReprWithKind<K, T>;
                    } else {
                        return createTermRepr(
                            null,
                            repr.children
                        ) as NodeReprWithKind<K, T>;
                    }
                }
                case NodeKind.Conj:
                case NodeKind.Disj:
                    return {
                        kind: newKind,
                        children: flattenChildren(newKind, repr.children),
                    } as NodeReprWithKind<K, T>;
                case NodeKind.Implies:
                case NodeKind.Equiv:
                    return {
                        kind: newKind,
                        lhs: repr,
                        rhs: createTermRepr(),
                    } as NodeReprWithKind<K, T>;
                default:
                    throw Error(
                        `Cannot convert kind ${oldKind} to kind ${newKind}`
                    );
            }
        }
        case NodeKind.Term: {
            switch (newKind) {
                case NodeKind.Value:
                    return {
                        kind: NodeKind.Value,
                        value: PredicateValue.Yes,
                        children: [repr],
                    } as NodeReprWithKind<K, T>;
                case NodeKind.Conj:
                case NodeKind.Disj:
                    if (repr.term === null) {
                        return {
                            kind: newKind,
                            children: flattenChildren(newKind, repr.children),
                        } as NodeReprWithKind<K, T>;
                    } else {
                        return {
                            kind: newKind,
                            children: [
                                createTermRepr(repr.term),
                                ...flattenChildren(newKind, repr.children),
                            ],
                        } as NodeReprWithKind<K, T>;
                    }
                case NodeKind.Implies:
                case NodeKind.Equiv:
                    return {
                        kind: newKind,
                        lhs: repr,
                        rhs: createTermRepr(),
                    } as NodeReprWithKind<K, T>;
                default:
                    throw Error(
                        `Cannot convert kind ${oldKind} to kind ${newKind}`
                    );
            }
        }
        case NodeKind.Conj:
        case NodeKind.Disj: {
            switch (newKind) {
                case NodeKind.Value: {
                    return {
                        kind: NodeKind.Value,
                        value:
                            oldKind === NodeKind.Conj
                                ? PredicateValue.Yes
                                : PredicateValue.No,
                        children: repr.children,
                    } as NodeReprWithKind<K, T>;
                }
                case NodeKind.Term: {
                    const [head, ...tail] = repr.children;
                    if (head && isTermRepr(head)) {
                        return createTermRepr(
                            head.term,
                            tail
                        ) as NodeReprWithKind<K, T>;
                    } else {
                        return createTermRepr(
                            null,
                            repr.children
                        ) as NodeReprWithKind<K, T>;
                    }
                }
                case NodeKind.Conj:
                case NodeKind.Disj: {
                    return {
                        kind: newKind,
                        children: flattenChildren(newKind, repr.children),
                    } as NodeReprWithKind<K, T>;
                }
                case NodeKind.Implies:
                case NodeKind.Equiv: {
                    return {
                        kind: newKind,
                        lhs: repr,
                        rhs: createTermRepr(),
                    } as NodeReprWithKind<K, T>;
                }
                default:
                    throw Error(
                        `Cannot convert kind ${oldKind} to kind ${newKind}`
                    );
            }
        }
        case NodeKind.Neg: {
            const child = repr.child;
            if (isReprWithKind(newKind, child)) return child;
            switch (newKind) {
                case NodeKind.Term:
                case NodeKind.Value: {
                    return reprWithNodeKind(newKind as K, child);
                }
                case NodeKind.Conj:
                case NodeKind.Disj: {
                    return {
                        kind: newKind,
                        children: [child],
                    } as NodeReprWithKind<K, T>;
                }
                case NodeKind.Implies:
                case NodeKind.Equiv: {
                    return {
                        kind: newKind,
                        lhs: child,
                        rhs: createTermRepr(),
                    } as NodeReprWithKind<K, T>;
                }
                default:
                    throw Error(
                        `Cannot convert kind ${oldKind} to kind ${newKind}`
                    );
            }
        }
        case NodeKind.Equiv:
        case NodeKind.Implies: {
            const { lhs, rhs } = repr;
            switch (newKind) {
                case NodeKind.Value: {
                    if (isValueRepr(lhs)) {
                        return {
                            kind: NodeKind.Value,
                            value: PredicateValue.Yes,
                            children: [rhs],
                        } as NodeReprWithKind<K, T>;
                    } else {
                        return {
                            kind: NodeKind.Value,
                            value: PredicateValue.Yes,
                            children: [lhs, rhs],
                        } as NodeReprWithKind<K, T>;
                    }
                }
                case NodeKind.Term: {
                    if (isTermRepr(lhs)) {
                        return createTermRepr(lhs.term, [
                            rhs,
                        ]) as NodeReprWithKind<K, T>;
                    } else if (isTermRepr(rhs)) {
                        return createTermRepr(rhs.term) as NodeReprWithKind<
                            K,
                            T
                        >;
                    } else {
                        return createTermRepr(null, [
                            lhs,
                            rhs,
                        ]) as NodeReprWithKind<K, T>;
                    }
                }
                case NodeKind.Conj:
                case NodeKind.Disj:
                    return {
                        kind: newKind,
                        children: flattenChildren(newKind, [lhs, rhs]),
                    } as NodeReprWithKind<K, T>;
                case NodeKind.Implies:
                case NodeKind.Equiv:
                    return {
                        kind: newKind,
                        lhs,
                        rhs,
                    } as NodeReprWithKind<K, T>;
                default:
                    throw Error(
                        `Cannot convert kind ${oldKind} to kind ${newKind}`
                    );
            }
        }
    }
}
